import React, {
  useState,
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import "./carousel.scss";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import {
  motion,
  DragControls,
  useMotionValue,
  useTransform,
  animate,
  useSpring,
  useMotionValueEvent,
} from "framer-motion";
import useDeviceDetection from "../../../hooks/useDeviceDetection";


const CircularCarousel = forwardRef(
  (
    {
      vh,
      carouselItems = [
        { linkTo: "", text: "", image: "", hoverImage: "" },
      ],
    },
    ref
  ) => {


    // Use this to detect if we’re on mobile
    const deviceType = useDeviceDetection();


    // -- Radius controls (in pixels) --
    const [radiusXuser, setUserRadiusX] = useState(570);
    const [radiusYuser, setUserRadiusY] = useState(300);

    const [radiusX, setRadiusX] = useState(radiusXuser * vh);
    const [radiusY, setRadiusY] = useState(radiusYuser * vh);

    // Number of visible items control
    const [visibleItemCount, setVisibleItemCount] = useState(
      Math.min(carouselItems.length, 6)
    );

    // Margin from the top for the carousel items
    const [itemMarginTop, setItemMarginTop] = useState(-5);

    const containerRef = useRef(null);

    // Drag/snap parameters
    const [dragSensitivity, setDragSensitivity] = useState(0.09);
    const [velocityFactor, setVelocityFactor] = useState(0.01);
    const [snapDamping, setSnapDamping] = useState(79);
    const [snapStiffness, setSnapStiffness] = useState(981);

    // Rotation spring parameters
    const [rotationStiffness, setRotationStiffness] = useState(300);
    const [rotationDamping, setRotationDamping] = useState(100);

    const baseRotation = useMotionValue(0);
    const rotation = useSpring(baseRotation, {
      stiffness: rotationStiffness,
      damping: rotationDamping,
    });
    const [dragging, setDragging] = useState(false);

    // Control panel toggle
    const [showControls, setShowControls] = useState(false);

    // Draggable control panel
    const dragControls = useRef(new DragControls()).current;

    // Recompute radii on vh changes
    useEffect(() => {
      const customX = Math.min(radiusXuser * vh, Math.abs(deviceType?.isMobile ? 0.62 * window.innerWidth : Math.max(vh * 5 * 107, window.innerWidth - 1000 * vh)));
      setRadiusX(customX);
      setRadiusY(radiusYuser * vh);
    }, [vh, radiusXuser, radiusYuser, deviceType]);

    // useEffect(() => {
    //   const customX = Math.min(radiusXuser * vh, Math.abs(deviceType?.isMobile ? 0.7 * window.innerWidth : window.innerWidth - 1000 * vh));
    //   setRadiusX(customX);
    //   setRadiusY(radiusYuser * vh);
    //   setItemMarginTop(deviceType?.isMobile ? 86 : -5)
    // }, [deviceType?.isMobile, vh, radiusXuser, radiusYuser]);


    // Listen for Alt+C to toggle control panel
    useEffect(() => {
      const handleKeyDown = (e) => {
        if (e.altKey && e.code === "KeyC") {
          setShowControls((prev) => !prev);
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }, []);

    // ---------------------------
    // NEW: Scroll handling setup
    // ---------------------------
    // Adjust this value to control how sensitive scrolling is.
    const scrollSensitivity = 0.1;
    const scrollTimeoutRef = useRef(null);

    const handleWheel = (e) => {
      // If mobile, you might want to disable wheel-based rotation
      // but let's keep it for tablets if you want:
      if (deviceType?.isMobile) return;

      // Rotate the carousel based on the wheel delta.
      // Scrolling down (deltaY positive) rotates to the right.
      // Scrolling up (deltaY negative) rotates to the left.
      baseRotation.set(baseRotation.get() - e.deltaY * scrollSensitivity);

      // Clear any existing timeout.
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      // When wheel events stop for 50ms, snap immediately to the nearest item.
      scrollTimeoutRef.current = setTimeout(() => {
        snapToClosestItem(0);
      }, 50);
    };

    const handleDrag = (_, info) => {
      setDragging(true);
      baseRotation.set(baseRotation.get() + info.delta.x * dragSensitivity);
    };

    const handleDragEnd = (_, info) => {
      const velocity = info.velocity.x;
      snapToClosestItem(velocity * velocityFactor);
    };

    const snapToClosestItem = (
      additionalRotation = 0,
      damping = snapDamping,
      stiffness = snapStiffness
    ) => {
      const anglePerItem = 360 / visibleItemCount;
      const targetRotation = baseRotation.get() + additionalRotation;
      const snappedRotation =
        Math.round(targetRotation / anglePerItem) * anglePerItem;

      animate(baseRotation, snappedRotation, {
        type: "spring",
        stiffness,
        damping,
      });
      setDragging(false);
    };

    useImperativeHandle(ref, () => ({
      next: () => {
        const anglePerItem = 360 / visibleItemCount;
        snapToClosestItem(anglePerItem);
      },
      prev: () => {
        const anglePerItem = 360 / visibleItemCount;
        snapToClosestItem(-anglePerItem);
      },
      tizzer: () => {
        const anglePerItem = 190;
        snapToClosestItem(anglePerItem, 100, 200);
      },
    }));

    return (
      <>
        {deviceType?.isMobile && (
          <>
            <button
              className="carousel-arrow carousel-arrow-left"
              onClick={() => ref.current?.prev()}
            >
              <svg  className="no-flip" xmlns="http://www.w3.org/2000/svg" width="13" height="14" viewBox="0 0 13 14" fill="none">
                <path d="M10.6453 2.16192C13.0918 -0.626381 4.61236 6.67198 1.10833 7.45599C-0.27853 7.76621 11.0008 10.9283 11.6431 12.6048" stroke="black" strokeWidth="1.7" strokeLinecap="round" />
              </svg>
            </button>
            <button
              className="carousel-arrow carousel-arrow-right"
              onClick={() => ref.current?.next()}
            >
              <svg className="no-flip"  xmlns="http://www.w3.org/2000/svg" width="13" height="14" viewBox="0 0 13 14" fill="none">
                <path d="M2.35465 2.16192C-0.0918162 -0.626381 8.38764 6.67198 11.8917 7.45599C13.2785 7.76621 1.99916 10.9283 1.35693 12.6048" stroke="black" strokeWidth="1.7" strokeLinecap="round" />
              </svg>
            </button>
          </>
        )}
        <div className={`carousel wrapper ${deviceType?.isTinyMobile ? 'tiny-mobile' : ''}`} ref={containerRef} onWheel={handleWheel}>
          {/* The main carousel content */}
          <motion.div
            className="content"
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0}
            onDrag={handleDrag}
            onDragEnd={handleDragEnd}
          >
            {carouselItems.slice(0, visibleItemCount).map((item, i) => {
              return (
                <CarouselItem
                  key={`carousel-item${i}`}
                  index={i}
                  total={visibleItemCount}
                  rotation={rotation}
                  radiusX={radiusX}
                  radiusY={radiusY}
                  isMobile={deviceType?.isMobile}
                  item={item}
                  dragging={dragging}
                  itemMarginTop={itemMarginTop}
                />
              );
            })}
          </motion.div>

          {/* Draggable Control panel */}
          {showControls && (
            <motion.div
              className="control-panel"
              drag
              dragListener={false}
              dragControls={dragControls}
              dragConstraints={{
                top: 0,
                left: 0,
                right: window.innerWidth - 150,
                bottom: window.innerHeight - 100,
              }}
            >
              <div
                className="drag-handle"
                onPointerDown={(e) => dragControls.start(e)}
              >
                <span className="drag-icon">⇕</span>
              </div>
              <button className="close-btn" onClick={() => setShowControls(false)}>
                X
              </button>
              <h3>לוח בקרה של הקרוסלה</h3>

              {/* Rotation stiffness */}
              <div className="control">
                <label>
                  <span>
                    קשיות סיבוב: {rotationStiffness}{" "}
                    <span
                      className="tooltip"
                      title="קשיות הסיבוב קובעת כמה חזק הקרוסלה נמשכת למיקום היעד שלה במהלך הסיבוב. ערך גבוה יגרום להחזרה מהירה יותר."
                    >
                      !
                    </span>
                  </span>
                  <input
                    type="range"
                    min="100"
                    max="1000"
                    step="10"
                    value={rotationStiffness}
                    onChange={(e) =>
                      setRotationStiffness(parseInt(e.target.value, 10))
                    }
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                  />
                </label>
              </div>

              {/* Rotation damping */}
              <div className="control">
                <label>
                  <span>
                    בלימת סיבוב: {rotationDamping}{" "}
                    <span
                      className="tooltip"
                      title="בלימת הסיבוב קובעת כמה מהר הקרוסלה נעצרת לאחר הגרירה. ערך גבוה יגרום להאטה מהירה יותר."
                    >
                      !
                    </span>
                  </span>
                  <input
                    type="range"
                    min="10"
                    max="100"
                    step="1"
                    value={rotationDamping}
                    onChange={(e) =>
                      setRotationDamping(parseInt(e.target.value, 10))
                    }
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                  />
                </label>
              </div>

              {/* Drag sensitivity */}
              <div className="control">
                <label>
                  <span>
                    רגישות לגרירה: {dragSensitivity.toFixed(2)}{" "}
                    <span
                      className="tooltip"
                      title="כמה מהר הקרוסלה מסתובבת כאשר אתה גורר אותה. ערך גבוה יגרום לסיבוב מהיר יותר."
                    >
                      !
                    </span>
                  </span>
                  <input
                    type="range"
                    min="0.01"
                    max="2"
                    step="0.001"
                    value={dragSensitivity}
                    onChange={(e) =>
                      setDragSensitivity(parseFloat(e.target.value))
                    }
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                  />
                </label>
              </div>

              {/* Velocity factor */}
              <div className="control">
                <label>
                  <span>
                    מכפיל מהירות: {velocityFactor.toFixed(2)}{" "}
                    <span
                      className="tooltip"
                      title="מקדם שמקבע את השפעת המהירות של הגרירה על סיבוב הקרוסלה בעת שחרור העכבר."
                    >
                      !
                    </span>
                  </span>
                  <input
                    type="range"
                    min="0.001"
                    max="0.5"
                    step="0.001"
                    value={velocityFactor}
                    onChange={(e) =>
                      setVelocityFactor(parseFloat(e.target.value))
                    }
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                  />
                </label>
              </div>

              {/* Snap damping */}
              <div className="control">
                <label>
                  <span>
                    בלימת Snap: {snapDamping}{" "}
                    <span
                      className="tooltip"
                      title="מספר זה משפיע על כמה מהר הקרוסלה נעצרת ומתייצבת לאחר סיום הגרירה."
                    >
                      !
                    </span>
                  </span>
                  <input
                    type="range"
                    min="10"
                    max="150"
                    step="1"
                    value={snapDamping}
                    onChange={(e) =>
                      setSnapDamping(parseInt(e.target.value, 10))
                    }
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                  />
                </label>
              </div>

              {/* Snap stiffness */}
              <div className="control">
                <label>
                  <span>
                    קשיות Snap: {snapStiffness}{" "}
                    <span
                      className="tooltip"
                      title="כמה חזק הקרוסלה נמשכת למיקום היעד שלה. ערך גבוה יגרום להחזרה מהירה יותר."
                    >
                      !
                    </span>
                  </span>
                  <input
                    type="range"
                    min="1"
                    max="1000"
                    step="10"
                    value={snapStiffness}
                    onChange={(e) =>
                      setSnapStiffness(parseInt(e.target.value, 10))
                    }
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                  />
                </label>
              </div>

              {/* New Control: Radius X */}
              <div className="control">
                <label>
                  <span>
                    רדיוס אופקי: {radiusXuser}px{" "}
                    <span
                      className="tooltip"
                      title="הרדיוס בציר האופקי משפיע על המרחק של הפריטים מהמרכז בצדדים."
                    >
                      !
                    </span>
                  </span>
                  <input
                    type="range"
                    min="10"
                    max="2000"
                    step="10"
                    value={radiusXuser}
                    onChange={(e) =>
                      setUserRadiusX(parseInt(e.target.value, 10))
                    }
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                  />
                </label>
              </div>

              {/* New Control: Radius Y */}
              <div className="control">
                <label>
                  <span>
                    רדיוס אנכי: {radiusYuser}px{" "}
                    <span
                      className="tooltip"
                      title="הרדיוס בציר האנכי משפיע על המרחק של הפריטים מהמרכז מלמעלה ומלמטה."
                    >
                      !
                    </span>
                  </span>
                  <input
                    type="range"
                    min="100"
                    max="1000"
                    step="10"
                    value={radiusYuser}
                    onChange={(e) =>
                      setUserRadiusY(parseInt(e.target.value, 10))
                    }
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                  />
                </label>
              </div>

              {/* New Control: Visible Item Count */}
              <div className="control">
                <label>
                  <span>
                    מספר פריטים: {visibleItemCount}{" "}
                    <span
                      className="tooltip"
                      title="קובע את מספר הפריטים שמוצגים בקרוסלה (בין 3 ל-9)."
                    >
                      !
                    </span>
                  </span>
                  <input
                    type="range"
                    min="3"
                    max="9"
                    step="1"
                    value={visibleItemCount}
                    onChange={(e) =>
                      setVisibleItemCount(parseInt(e.target.value, 10))
                    }
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                  />
                </label>
              </div>

              {/* New Control: Top Margin for Items */}
              <div className="control">
                <label>
                  <span>
                    מרווח מהחלק העליון: {itemMarginTop}px{" "}
                    <span
                      className="tooltip"
                      title="קובע את המרווח של הפריטים מהחלק העליון של הקרוסלה."
                    >
                      !
                    </span>
                  </span>
                  <input
                    type="range"
                    min="-100"
                    max="200"
                    step="1"
                    value={itemMarginTop}
                    onChange={(e) => setItemMarginTop(parseInt(e.target.value, 10))}
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                  />
                </label>
              </div>
            </motion.div>
          )}
        </div>
      </>
    );
  }
);

function CarouselItem({
  index,
  total,
  rotation,
  radiusX,
  radiusY,
  item,
  isMobile,
  dragging,
  itemMarginTop,
}) {
  const navigate = useNavigate();
  const [t] = useTranslation();
  const anglePerItem = 360 / total;
  const offset = index * anglePerItem;
  const itemRotation = useTransform(rotation, (r) => r + offset);

  const x = useTransform(itemRotation, (r) => {
    const normalized = Math.abs((r % 360 + 360) % 360);
    const radiusFactor = 1;//0.99 + 0.01 * Math.cos((normalized * Math.PI) / 180);
    return Math.sin((r * Math.PI) / 180) * radiusX * radiusFactor;
  });

  const y = useTransform(itemRotation, (r) => {
    const normalized = Math.abs((r % 360 + 360) % 360);
    const radiusFactor = 0.7 + 0.3 * Math.cos((normalized * Math.PI) / 180);
    return Math.cos((r * Math.PI) / 180) * radiusY * radiusFactor;
  });

  // Add the top margin to the Y coordinate
  const yWithMargin = useTransform(y, (v) => v + itemMarginTop);

  const scale = useTransform(itemRotation, (r) => {
    const normalized = Math.abs((r % 360 + 360) % 360);
    return (isMobile ? 0.5 : 0.6) + ((isMobile ? 0.35 : 0.45) * Math.cos((normalized * Math.PI) / 180));
  });

  const zIndex = useTransform(itemRotation, (r) => {
    const normalized = Math.abs((r % 360 + 360) % 360);
    return 0.2 + 0.45 * Math.cos((normalized * Math.PI) / 180) * 100;
  });

  const opacity = useTransform(itemRotation, (r) => {
    const normalized = Math.abs((r % 360 + 360) % 360);
    return 0.6 + 0.4 * Math.cos((normalized * Math.PI) / 180);
  });

  // Determine if this item is in "front" (to allow clicking)
  const angle = useTransform(itemRotation, (r) => {
    const normalized = Math.abs((r % 360 + 360) % 360);
    const anglePerItem = 360 / total;
    const frontRange = anglePerItem * 1.5;
    const frontRange1 = anglePerItem * 0.7;
    var l = normalized >= 360 - frontRange1 || normalized <= frontRange1;
    if (l)
      return 1;
    if (normalized >= 360 - frontRange || normalized <= frontRange)
      return 2;
    return 0
  });

  const [className, setClassName] = useState(
    angle.get() > 0 ? `clickable-item item ${angle.get() > 1 ? "desktop-clickable-item" : ""}` : "item"
  );

  useMotionValueEvent(angle, "change", (latest) => {
    setClassName(latest > 0 ? `clickable-item item ${latest > 1 ? "desktop-clickable-item" : ""}` : "item");
  });

  const handleClick = () => {
    if (!dragging) {
      navigate(item.linkTo);
    }
  };

  return (
    <motion.div
      className={className}
      style={{ x, y: yWithMargin, scale, opacity, zIndex }}
      onClick={handleClick}
    >
      <button
        className="carousel-item"
        style={{
          "--background-image": `url(${item.image})`,
          "--hover-background-image": `url(${item.hoverImage})`,
        }}
      />
      <button className="carousel-btn">{t(`${item.text}`)}</button>
    </motion.div>
  );
}

export default CircularCarousel;
